import { useState, useRef } from 'react'
import { X, Package, Loader2, Save } from 'lucide-react'
import { shipping, ShipmentProduct } from '@/lib/api'
import { useMutation } from '@tanstack/react-query'

interface AddProductModalProps {
  orderId: number
  onClose: () => void
  onSuccess: () => void
  editingProduct?: ShipmentProduct | null
}

export default function AddProductModal({ orderId, onClose, onSuccess, editingProduct }: AddProductModalProps) {
  const [formData, setFormData] = useState({
    product_name: editingProduct?.product_name || '',
    product_reference: editingProduct?.product_reference || '',
    quantity: editingProduct?.quantity || 1,
    price: editingProduct?.price || 0,
    weight: editingProduct?.weight || 0,
    image_url: editingProduct?.image_url || ''
  })

  const [previewImage, setPreviewImage] = useState<string | null>(editingProduct?.image_url || null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const addProductMutation = useMutation({
    mutationFn: () => shipping.addShipmentProduct(orderId, formData),
    onSuccess: () => onSuccess()
  })

  const updateProductMutation = useMutation({
    mutationFn: () => editingProduct
      ? shipping.updateShipmentProduct(orderId, editingProduct.id, formData)
      : Promise.reject('No product to edit'),
    onSuccess: () => onSuccess()
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.product_name.trim()) return

    if (editingProduct) {
      updateProductMutation.mutate()
    } else {
      addProductMutation.mutate()
    }
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Preview local
      const reader = new FileReader()
      reader.onload = () => {
        setPreviewImage(reader.result as string)
      }
      reader.readAsDataURL(file)

      // TODO: Subir imagen al servidor y obtener URL
      // Por ahora solo mostramos preview local
    }
  }

  const isLoading = addProductMutation.isPending || updateProductMutation.isPending
  const total = formData.price * formData.quantity

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {editingProduct ? 'Editar producto' : 'Añadir producto al envío'}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSubmit}
              disabled={isLoading || !formData.product_name.trim()}
              className="p-2 text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Guardar"
            >
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Contenido del formulario */}
        <form onSubmit={handleSubmit} className="p-5">
          <div className="flex gap-5">
            {/* Imagen del producto */}
            <div className="flex-shrink-0">
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-28 h-28 bg-slate-100 dark:bg-slate-700 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center cursor-pointer hover:border-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/10 transition-colors overflow-hidden"
              >
                {previewImage ? (
                  <img src={previewImage} alt="Preview" className="w-full h-full object-contain" />
                ) : (
                  <div className="text-center">
                    <Package className="w-8 h-8 text-slate-400 mx-auto" />
                    <span className="text-[10px] text-slate-400 mt-1 block">+ Añadir<br/>imagen</span>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>

            {/* Campos del formulario */}
            <div className="flex-1 space-y-3">
              {/* Nombre del producto */}
              <div>
                <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Producto *
                </label>
                <input
                  type="text"
                  value={formData.product_name}
                  onChange={e => setFormData({ ...formData, product_name: e.target.value })}
                  placeholder="Nombre del producto"
                  required
                  className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 placeholder:text-slate-400"
                />
              </div>

              {/* Referencia */}
              <div>
                <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Referencia
                </label>
                <input
                  type="text"
                  value={formData.product_reference}
                  onChange={e => setFormData({ ...formData, product_reference: e.target.value })}
                  placeholder="SKU / Referencia"
                  className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 placeholder:text-slate-400"
                />
              </div>

              {/* Cantidad y Precio */}
              <div className="flex gap-3">
                <div className="w-20">
                  <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Cantidad
                  </label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={e => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                    min="1"
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Precio unitario
                  </label>
                  <div className="relative mt-1">
                    <input
                      type="number"
                      value={formData.price}
                      onChange={e => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 pr-8 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                  </div>
                </div>
              </div>

              {/* Peso */}
              <div>
                <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Peso
                </label>
                <div className="relative mt-1">
                  <input
                    type="number"
                    value={formData.weight}
                    onChange={e => setFormData({ ...formData, weight: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.001"
                    placeholder="0.000"
                    className="w-full px-3 py-2 pr-10 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 placeholder:text-slate-400"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">kg</span>
                </div>
              </div>
            </div>
          </div>

          {/* Total */}
          <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <span className="text-sm text-slate-500 dark:text-slate-400">Total:</span>
            <span className="text-xl font-bold text-slate-900 dark:text-white">€{total.toFixed(2)}</span>
          </div>

          {/* Error */}
          {(addProductMutation.error || updateProductMutation.error) && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">
                Error: {String((addProductMutation.error || updateProductMutation.error) as any)}
              </p>
            </div>
          )}

          {/* Nota informativa */}
          <p className="mt-4 text-[11px] text-slate-400 dark:text-slate-500 text-center">
            Este producto se añade solo para la gestión del envío.<br/>
            No modifica el pedido original en PrestaShop.
          </p>
        </form>
      </div>
    </div>
  )
}
